import { defineStore } from 'pinia'
import { storeToRefs } from 'pinia'
import { useMqttStore } from '@/store/mqttStore'
import { useMeasurementStore } from '@/store/measurementStore'
import { usePreferenceStore } from '@/store/preferenceStore'
import { MqttValue, MqttMessage } from '../mqttDefinitions'
import simulateService from '@/service/simulateService'
import calculateService from '@/service/calculateService'
import type { position, calibratedHardware } from '@/types/position'
import {
  handlebarCurrentPositionX,
  handlebarCurrentPositionY,
  saddleCurrentPositionX,
  saddleCurrentPositionY,
} from '@/mqttDefinitions'
import {
  saddleCurrentSetPointX,
  saddleCurrentSetPointY,
  handlebarCurrentSetPointX,
  handlebarCurrentSetPointY,
} from '@/mqttDefinitions'
import { MqttTopic } from '@/mqttDefinitions'
import { until } from '@vueuse/core'
import { sleep } from '@/utils/utils'

const numberFormatter = new Intl.NumberFormat('en-US', {
  signDisplay: 'auto',
})

export const usePositionStore = defineStore('positionStore', {
  state: (): {
    startPosition: position
    positionOffset: position
    positionOffsetGear: position
    positionDeviation: position
    isCalibrated: boolean
    isCalibrating: boolean
    calibratedHardware?: calibratedHardware
  } => ({
    // Start position as recieved from BFC/entered by bike dealer
    startPosition: {
      handlebarX: 0,
      handlebarY: 0,
      saddleX: 0,
      saddleY: 0,
    },

    // Difference between motorPosition and absolutePosition
    positionOffset: {
      handlebarX: 0,
      handlebarY: 0,
      saddleX: 0,
      saddleY: 0,
    },

    // Difference between motorPosition and absolutePosition after second calibration
    positionOffsetGear: {
      handlebarX: 0,
      handlebarY: 0,
      saddleX: 0,
      saddleY: 0,
    },

    // Difference between measuredPosition and absolutePosition
    positionDeviation: {
      handlebarX: 0,
      handlebarY: 0,
      saddleX: 0,
      saddleY: 0,
    },

    isCalibrated: false,
    isCalibrating: false,
    calibratedHardware: undefined,
  }),

  getters: {
    // Current motor position as received from the controller (in mm!)
    motorPosition(): position {
      const mqttStore = useMqttStore()
      return {
        handlebarX: mqttStore.getMessageOnTopic(handlebarCurrentPositionX),
        handlebarY: mqttStore.getMessageOnTopic(handlebarCurrentPositionY),
        saddleX: mqttStore.getMessageOnTopic(saddleCurrentPositionX),
        saddleY: mqttStore.getMessageOnTopic(saddleCurrentPositionY),
      } as position
    },

    // Set point motor as received from the controller (in mm!)
    motorSetPoint(): position {
      const mqttStore = useMqttStore()
      return {
        handlebarX: mqttStore.getMessageOnTopic(handlebarCurrentSetPointX),
        handlebarY: mqttStore.getMessageOnTopic(handlebarCurrentSetPointY),
        saddleX: mqttStore.getMessageOnTopic(saddleCurrentSetPointX),
        saddleY: mqttStore.getMessageOnTopic(saddleCurrentSetPointY),
      } as position
    },

    // Current position as measured by the XY-tool
    measuredPosition(): position {
      return {
        handlebarX:
          this.absolutePosition.handlebarX +
          this.positionDeviation.handlebarX * -1,
        handlebarY:
          this.absolutePosition.handlebarY + this.positionDeviation.handlebarY,
        saddleX: this.absolutePosition.saddleX + this.positionDeviation.saddleX,
        saddleY: this.absolutePosition.saddleY + this.positionDeviation.saddleY,
      } as position
    },

    // Current position as determined by the motor position and the corresponding offset
    // Since the handlebar X moves towards the 0 point, it's value is subtracted from the offset
    absolutePosition(): position {
      const preferenceStore = usePreferenceStore()
      const { hasPositioningMotors } = storeToRefs(preferenceStore)

      return {
        handlebarX:
          this.positionOffset.handlebarX +
          this.positionOffsetGear.handlebarX +
          (hasPositioningMotors.value
            ? this.motorPosition.handlebarX
            : this.motorPosition.handlebarX * -1),
        handlebarY:
          this.positionOffset.handlebarY +
          this.positionOffsetGear.handlebarY +
          this.motorPosition.handlebarY,
        saddleX:
          this.positionOffset.saddleX +
          this.positionOffsetGear.saddleX +
          this.motorPosition.saddleX,
        saddleY:
          this.positionOffset.saddleY +
          this.positionOffsetGear.saddleY +
          this.motorPosition.saddleY,
      } as position
    },

    // Difference between startPosition and absolutePosition
    positionChange(): any {
      return {
        handlebarX: numberFormatter.format(
          this.absolutePosition.handlebarX - this.startPosition.handlebarX,
        ),
        handlebarY: numberFormatter.format(
          this.absolutePosition.handlebarY - this.startPosition.handlebarY,
        ),
        saddleX: numberFormatter.format(
          this.absolutePosition.saddleX - this.startPosition.saddleX,
        ),
        saddleY: numberFormatter.format(
          this.absolutePosition.saddleY - this.startPosition.saddleY,
        ),
      } as any
    },

    motorReachedSetpoint(): boolean {
      const preferenceStore = usePreferenceStore()
      const { hasPositioningMotors } = storeToRefs(preferenceStore)
      const margin = 2

      if (!hasPositioningMotors.value) {
        return true
      } else {
        return (
          Math.abs(
            this.motorPosition.handlebarX - this.motorSetPoint.handlebarX,
          ) <= margin &&
          Math.abs(
            this.motorPosition.handlebarY - this.motorSetPoint.handlebarY,
          ) <= margin &&
          Math.abs(this.motorPosition.saddleX - this.motorSetPoint.saddleX) <=
            margin &&
          Math.abs(this.motorPosition.saddleY - this.motorSetPoint.saddleY) <=
            margin
        )
      }
    },

    motorUndoneMovement(): position {
      return {
        handlebarX:
          this.motorPosition.handlebarX - this.motorSetPoint.handlebarX,
        handlebarY:
          this.motorPosition.handlebarY - this.motorSetPoint.handlebarY,
        saddleX: this.motorPosition.saddleX - this.motorSetPoint.saddleX,
        saddleY: this.motorPosition.saddleY - this.motorSetPoint.saddleY,
      }
    },

    isLatestMeasurementCalibrated(): boolean {
      const preferenceStore = usePreferenceStore()
      const measurementStore = useMeasurementStore()
      const { hasPositioningMotors } = storeToRefs(preferenceStore)
      const { latestMeasurement } = storeToRefs(measurementStore)

      if (hasPositioningMotors.value) {
        const isCalibrated =
          latestMeasurement.value.crankset.active ===
            this.calibratedHardware?.crankset &&
          latestMeasurement.value.handlebar.active ===
            this.calibratedHardware?.handlebar &&
          latestMeasurement.value.saddle.active ===
            this.calibratedHardware?.saddle &&
          latestMeasurement.value.pedal.active ===
            this.calibratedHardware?.pedal
        return isCalibrated
      } else {
        return true
      }
    },
  },

  actions: {
    submitPosition(definition: MqttValue, newValue: number): boolean {
      const preferenceStore = usePreferenceStore()
      const { hasPositioningMotors } = storeToRefs(preferenceStore)

      if (isNaN(newValue)) {
        return false
      }

      if (hasPositioningMotors.value) {
        const mqttStore = useMqttStore()
        const newPosition = {
          handlebarX:
            definition === handlebarCurrentSetPointX
              ? newValue
              : this.motorSetPoint.handlebarX,
          handlebarY:
            definition === handlebarCurrentSetPointY
              ? newValue
              : this.motorSetPoint.handlebarY,
          saddleX:
            definition === saddleCurrentSetPointX
              ? newValue
              : this.motorSetPoint.saddleX,
          saddleY:
            definition === saddleCurrentSetPointY
              ? newValue
              : this.motorSetPoint.saddleY,
        }

        if (calculateService.validateMotorPosition(newPosition)) {
          mqttStore.doPublish(
            {
              topic: definition.topic,
              payload: newValue,
            } as MqttMessage,
            definition,
            mqttStore.client,
          )
          return true
        }
        return false
      } else {
        simulateService.publish(definition, newValue)
        return true
      }
    },

    async sendCalibrationToMotors(motorPosition: position): Promise<boolean> {
      const preferenceStore = usePreferenceStore()
      const { hasPositioningMotors } = storeToRefs(preferenceStore)

      this.submitPosition(handlebarCurrentSetPointX, motorPosition.handlebarX)
      this.submitPosition(handlebarCurrentSetPointY, motorPosition.handlebarY)
      this.submitPosition(saddleCurrentSetPointX, motorPosition.saddleX)
      this.submitPosition(saddleCurrentSetPointY, motorPosition.saddleY)

      if (hasPositioningMotors.value) {
        this.isCalibrating = true

        await sleep(200)
        await until(() => this.motorReachedPoint(motorPosition)).toBe(true)

        this.isCalibrating = false
        return true
      } else {
        return true
      }
    },

    async initialCalibrate(): Promise<boolean> {
      const result = await calculateService.initialCalibration(
        this.positionDeviation,
        this.motorPosition,
        this.startPosition,
      )

      if (result) {
        await this.sendCalibrationToMotors(result.motorPosition)
        this.updateCalibratedHardware()

        this.positionOffset = result.positionOffset
        this.isCalibrated = true
        return true
      } else {
        return false
      }
    },

    async updatedCalibrate(): Promise<boolean> {
      const result = await calculateService.updatedCalibration(
        this.positionDeviation,
        this.motorPosition,
        this.absolutePosition,
      )

      if (result) {
        await this.sendCalibrationToMotors(result.motorPosition)
        this.updateCalibratedHardware()

        this.positionOffsetGear.handlebarX += result.positionChange.handlebarX
        this.positionOffsetGear.handlebarY += result.positionChange.handlebarY
        this.positionOffsetGear.saddleX += result.positionChange.saddleX
        this.positionOffsetGear.saddleY += result.positionChange.saddleY
        this.isCalibrated = true
        return true
      } else {
        return false
      }
    },

    async noMotorsCalibrate(): Promise<boolean> {
      const result = await calculateService.noMotorsCalibration(
        this.positionDeviation,
      )

      if (result) {
        await this.sendCalibrationToMotors(result.motorPosition)
        this.updateCalibratedHardware()

        this.isCalibrated = true
        return true
      } else {
        return false
      }
    },

    async updateCalibratedHardware() {
      const measurementStore = useMeasurementStore()
      const { latestMeasurement } = storeToRefs(measurementStore)

      this.calibratedHardware = {
        crankset: latestMeasurement.value.crankset.active,
        handlebar: latestMeasurement.value.handlebar.active,
        saddle: latestMeasurement.value.saddle.active,
        pedal: latestMeasurement.value.pedal.active,
      }
    },

    async cancelCalibration() {
      if (!this.isCalibrated) {
        this.positionOffsetGear.handlebarX +=
          this.motorUndoneMovement.handlebarX
        this.positionOffsetGear.handlebarY +=
          this.motorUndoneMovement.handlebarY
        this.positionOffsetGear.saddleX += this.motorUndoneMovement.saddleX
        this.positionOffsetGear.saddleY += this.motorUndoneMovement.saddleY
      }

      this.cancelMovement()
      this.isCalibrating = false
    },

    async cancelMovement() {
      this.submitPosition(
        handlebarCurrentSetPointX,
        this.motorPosition.handlebarX,
      )
      this.submitPosition(
        handlebarCurrentSetPointY,
        this.motorPosition.handlebarY,
      )
      this.submitPosition(saddleCurrentSetPointX, this.motorPosition.saddleX)
      this.submitPosition(saddleCurrentSetPointY, this.motorPosition.saddleY)
    },

    resetPosition() {
      this.submitPosition(handlebarCurrentSetPointX, 0)
      this.submitPosition(handlebarCurrentSetPointY, 0)
      this.submitPosition(saddleCurrentSetPointX, 0)
      this.submitPosition(saddleCurrentSetPointY, 0)

      this.resetCalibrationValues()
    },

    resetCalibrationValues() {
      this.isCalibrating = false
      this.isCalibrated = false

      this.positionOffset = {
        handlebarX: 0,
        handlebarY: 0,
        saddleX: 0,
        saddleY: 0,
      }

      this.positionOffsetGear = {
        handlebarX: 0,
        handlebarY: 0,
        saddleX: 0,
        saddleY: 0,
      }

      this.positionDeviation = {
        handlebarX: 0,
        handlebarY: 0,
        saddleX: 0,
        saddleY: 0,
      }
    },

    sendLastPosition() {
      this.submitPosition(
        handlebarCurrentSetPointX,
        this.motorSetPoint.handlebarX,
      )
      this.submitPosition(
        handlebarCurrentSetPointY,
        this.motorSetPoint.handlebarY,
      )
      this.submitPosition(saddleCurrentSetPointX, this.motorSetPoint.saddleX)
      this.submitPosition(saddleCurrentSetPointY, this.motorSetPoint.saddleY)
    },

    startHoming() {
      const mqttStore = useMqttStore()
      const { doRawPublish, client } = mqttStore

      doRawPublish(
        MqttTopic.MOTORCONTROLLER_HOMING_SET,
        JSON.stringify({}),
        client,
      )
    },

    doResetMotors() {
      const mqttStore = useMqttStore()
      const { doRawPublish, client } = mqttStore

      doRawPublish(MqttTopic.MOTORCONTROLLER_RESET, JSON.stringify({}), client)
    },

    doReboot() {
      const mqttStore = useMqttStore()
      const { doRawPublish, client } = mqttStore

      doRawPublish(MqttTopic.CONTROLLER_REBOOT_SET, JSON.stringify({}), client)
    },

    doShutdown() {
      const mqttStore = useMqttStore()
      const { doRawPublish, client } = mqttStore

      doRawPublish(
        MqttTopic.CONTROLLER_SHUTDOWN_SET,
        JSON.stringify({}),
        client,
      )
    },

    motorReachedPoint(newPosition: position): boolean {
      const preferenceStore = usePreferenceStore()
      const { hasPositioningMotors } = storeToRefs(preferenceStore)

      if (!hasPositioningMotors.value) {
        return true
      } else {
        return (
          this.motorPosition.handlebarX === newPosition.handlebarX &&
          this.motorPosition.handlebarY === newPosition.handlebarY &&
          this.motorPosition.saddleX === newPosition.saddleX &&
          this.motorPosition.saddleY === newPosition.saddleY
        )
      }
    },
  },
  persist: {
    storage: sessionStorage,
  },
})
